import { useState, useEffect } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminDashboard({ credentials, userRole }) {
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    upcomingEvents: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    // Only fetch if credentials are available and data hasn't been loaded
    if (!credentials.username || !credentials.password) {
      return;
    }

    const auth = btoa(`${credentials.username}:${credentials.password}`);
    const headers = { Authorization: `Basic ${auth}` };

    const fetchData = async () => {
      setLoading(true);
      try {
        const [bookingsRes, eventsRes, messagesRes] = await Promise.all([
          axios.get(`${API}/bookings`, { headers }),
          axios.get(`${API}/events/all`, { headers }),
          axios.get(`${API}/contact`, { headers })
        ]);

        const bookingsData = bookingsRes.data;
        const eventsData = eventsRes.data;
        
        setAllBookings(bookingsData);
        setAllEvents(eventsData);
        
        const pendingCount = bookingsData.filter(b => b.status === "pending").length;
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const upcomingCount = eventsData.filter(e => e.event_date >= today).length;
        const unreadCount = messagesRes.data.filter(m => !m.is_read).length;

        setStats({
          totalBookings: bookingsData.length,
          pendingBookings: pendingCount,
          upcomingEvents: upcomingCount,
          unreadMessages: unreadCount
        });
        setDataLoaded(true);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [credentials.username, credentials.password]);

  // Format date to YYYY-MM-DD in local timezone
  const formatDateStr = (date) => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const activityDates = [...new Set([
    ...allBookings.filter(b => b.status !== "cancelled").map(b => b.booking_date),
    ...allEvents.map(e => e.event_date)
  ])].map(d => new Date(d + 'T12:00:00'));

  const selectedDateStr = formatDateStr(selectedDate);
  const dayBookings = selectedDateStr ? allBookings.filter(b => b.booking_date === selectedDateStr && b.status !== "cancelled") : [];
  const dayEvents = selectedDateStr ? allEvents.filter(e => e.event_date === selectedDateStr) : [];

  const formatDateDisplay = (d) => d?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid="admin-dashboard-content">
      <h1 className="heading-lg mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="card-heritage">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] mb-1">Total Bookings</p>
                <p className="text-3xl font-bold font-mono text-[#1C1917]">
                  {loading ? <span className="animate-pulse">...</span> : stats.totalBookings}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#14532D] rounded-lg flex items-center justify-center text-white">
                <Calendar className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-heritage">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] mb-1">Pending Bookings</p>
                <p className="text-3xl font-bold font-mono text-[#1C1917]">
                  {loading ? <span className="animate-pulse">...</span> : stats.pendingBookings}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#D97706] rounded-lg flex items-center justify-center text-white">
                <Clock className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-heritage">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] mb-1">Upcoming Events</p>
                <p className="text-3xl font-bold font-mono text-[#1C1917]">
                  {loading ? <span className="animate-pulse">...</span> : stats.upcomingEvents}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#0369A1] rounded-lg flex items-center justify-center text-white">
                <CalendarDays className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-heritage">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#78716C] mb-1">Unread Messages</p>
                <p className="text-3xl font-bold font-mono text-[#1C1917]">
                  {loading ? <span className="animate-pulse">...</span> : stats.unreadMessages}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#78350F] rounded-lg flex items-center justify-center text-white">
                <Mail className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="card-heritage">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#14532D]" />
              Booking Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <CalendarPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                modifiers={{ hasActivity: activityDates }}
                modifiersStyles={{ hasActivity: { backgroundColor: '#DCFCE7', color: '#15803D', fontWeight: 'bold' } }}
                className="rounded-md border"
              />
            </div>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#DCFCE7] rounded"></div>
                <span className="text-[#78716C]">Has bookings/events</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-heritage">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#14532D]" />
              {selectedDate ? formatDateDisplay(selectedDate) : "Select a Date"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
                <p className="text-[#78716C]">Click on a date to see bookings and events</p>
              </div>
            ) : (
              <div className="space-y-4">
                {dayEvents.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-[#14532D] mb-2">Events</h4>
                    {dayEvents.map((event) => (
                      <div key={event.id} className="p-3 bg-[#EFF6FF] rounded-lg border border-[#BFDBFE] mb-2">
                        <p className="font-semibold text-[#1E40AF]">{event.title}</p>
                        <p className="text-sm text-[#3B82F6]">{event.start_time} - {event.end_time}</p>
                      </div>
                    ))}
                  </div>
                )}
                {dayBookings.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-[#14532D] mb-2">Bookings</h4>
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className={`p-3 rounded-lg border mb-2 ${booking.is_internal ? 'bg-[#FEF3C7] border-[#FCD34D]' : 'bg-[#F5F5F0] border-[#E7E5E4]'}`}>
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-[#1C1917]">
                            {booking.is_internal ? booking.purpose_of_hire?.replace("Internal: ", "") : booking.purpose_of_hire}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded ${booking.status === 'confirmed' ? 'bg-[#DCFCE7] text-[#15803D]' : booking.status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#FEE2E2] text-[#B91C1C]'}`}>
                            {booking.status}
                          </span>
                        </div>
                        <p className="text-sm text-[#78716C]">{booking.start_time} - {booking.end_time}</p>
                        {booking.is_internal && <span className="text-xs bg-[#78350F]/10 text-[#78350F] px-2 py-0.5 rounded">Internal</span>}
                      </div>
                    ))}
                  </div>
                )}
                    {dayEvents.length === 0 && dayBookings.length === 0 && (
                      <div className="text-center py-8">
                        <CheckCircle className="w-12 h-12 text-[#15803D] mx-auto mb-4" />
                        <p className="text-[#15803D] font-semibold">Date Available</p>
                        <p className="text-sm text-[#78716C]">No bookings or events on this date</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
    </div>
  );
}

// Bookings Component
